import React, { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react';
import { fetchTextsApi } from '../api/api';

// Define the structure of our texts object based on the mock data
interface TextsData {
  texts: {
    general: {
      appTitle: string;
      headerMessageWelcome: string;
      headerMessage: string;
    };
    start: {
      disclaimer1: string;
      disclaimer2: string;
      disclaimer3: string;
      disclaimer4: string;
      disclaimer1mobile: string;
      disclaimer2mobile: string;
      disclaimer3mobile: string;
      disclaimer4mobile: string;
      disclaimer5mobile: string;
      eyecatcherMessage: string;
      showEyecatcher: boolean;
    };
    chat: {
      consentButtonText: string;
      sendButtonText: string;
      placeholder: string;
      placeholderMultilang: string[];
      placeholderTokenLimitReached: string;
      error: {
        tokenLimitReached: string;
      };
      buttons: {
        copy: string;
        feedbackButton: string;
      };
      feedbackModal: {
        title: string;
        detailsLabel: string;
        detailsOptional: string;
        placeholder: string;
        cancelButton: string;
        sendButton: string;
        options: string[];
      };
    };
    navigation: {
      home: string;
      homeUrl: string;
      dataPrivacy: string;
      dataPrivacyUrl: string;
      legalNotice: string;
      legalNoticeUrl: string;
      usageConsent: string;
    };
    usageDisclaimer: {
      title: string;
      section1: {
        title: string;
        content: string;
      };
      section2: {
        title: string;
        content: string[];
      };
      section3: {
        title: string;
        content: string;
      };
      section4: {
        title: string;
        content: string;
      };
      section5: {
        title: string;
        content: string;
        linkText: string;
        linkUrl: string;
      };
    };
  };
}

interface TextContextType {
  texts: TextsData | null;
  isLoading: boolean;
  error: Error | null;
}

const TextContext = createContext<TextContextType | undefined>(undefined);

interface TextProviderProps {
  children: ReactNode;
}

export const TextProvider: React.FC<TextProviderProps> = ({ children }) => {
  const [texts, setTexts] = useState<TextsData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadTexts = async () => {
      try {
        setIsLoading(true);
        const response = await fetchTextsApi();
        
        if (!response.ok) {
          throw new Error(`Failed to fetch texts: ${response.statusText}`);
        }
        
        const data = await response.json();
        setTexts(data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('An unknown error occurred'));
        console.error('Error loading texts:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadTexts();
  }, []);

  // Use useMemo to prevent the context value from changing on every render
  const contextValue = useMemo(() => {
    return { texts, isLoading, error };
  }, [texts, isLoading, error]);

  return (
    <TextContext.Provider value={contextValue}>
      {children}
    </TextContext.Provider>
  );
};

// Custom hook to use the text context
export const useTexts = (): TextContextType => {
  const context = useContext(TextContext);
  if (context === undefined) {
    throw new Error('useTexts must be used within a TextProvider');
  }
  return context;
};
